// src/pages/Stock/Stock.jsx

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, onSnapshot, query, writeBatch, doc, getDocs, where, orderBy, serverTimestamp, increment } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import ImageUpload from '../../components/ImageUpload';
import BarcodePrintModal from '../../components/BarcodePrintModal';

const styles = {
    container: {
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        background: 'transparent'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '32px'
    },
    headerLeft: {
        display: 'flex',
        flexDirection: 'column'
    },
    title: {
        fontSize: '32px',
        fontWeight: '700',
        color: '#0f172a',
        margin: 0,
        letterSpacing: '-0.5px'
    },
    subtitle: {
        fontSize: '16px',
        color: '#64748b',
        marginTop: '4px'
    },
    headerActions: {
        display: 'flex',
        gap: '12px'
    },
    button: {
        padding: '12px 24px',
        border: 'none',
        background: 'linear-gradient(135deg, #06C167, #8b5cf6)',
        color: 'white',
        borderRadius: '12px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '600',
        transition: 'all 0.2s ease-in-out',
        boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)'
    },
    buttonSecondary: {
        background: 'white',
        color: '#64748b',
        border: '1px solid #e2e8f0',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    box: {
        background: 'white',
        padding: '32px',
        borderRadius: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e2e8f0'
    },
    statsRow: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '20px',
        marginBottom: '32px'
    },
    statCard: {
        background: 'white',
        padding: '20px',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        textAlign: 'center'
    },
    statValue: {
        fontSize: '24px',
        fontWeight: '700',
        color: '#0f172a',
        marginBottom: '4px'
    },
    statLabel: {
        fontSize: '12px',
        color: '#64748b',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        fontWeight: '500'
    },
    searchContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
        gap: '16px'
    },
    searchInputContainer: {
        position: 'relative',
        flex: 1,
        maxWidth: '400px'
    },
    searchInput: {
        width: '100%',
        padding: '12px 16px 12px 44px',
        fontSize: '14px',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        background: '#f8fafc',
        outline: 'none',
        transition: 'all 0.2s',
        boxSizing: 'border-box'
    },
    searchIcon: {
        position: 'absolute',
        left: '14px',
        top: '50%',
        transform: 'translateY(-50%)',
        color: '#94a3b8',
        width: '16px',
        height: '16px'
    },
    tableContainer: {
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        overflow: 'hidden'
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse'
    },
    th: {
        background: '#f8fafc',
        padding: '16px 20px',
        textAlign: 'left',
        borderBottom: '1px solid #e2e8f0',
        color: '#374151',
        fontSize: '12px',
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        cursor: 'pointer'
    },
    td: {
        padding: '16px 20px',
        borderBottom: '1px solid #f1f5f9',
        color: '#374151',
        fontSize: '14px'
    },
    trHover: {
        cursor: 'pointer',
        transition: 'background-color 0.2s'
    },
    productName: {
        fontWeight: '600',
        color: '#0f172a'
    },
    productImage: {
        width: '40px',
        height: '40px',
        borderRadius: '8px',
        objectFit: 'cover',
        border: '1px solid #e2e8f0'
    },
    criticalStock: {
        color: '#dc2626',
        fontWeight: '600',
        background: '#fef2f2',
        padding: '4px 8px',
        borderRadius: '6px',
        fontSize: '12px'
    },
    normalStock: {
        color: '#059669',
        fontWeight: '600'
    },
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 100,
        backdropFilter: 'blur(4px)'
    },
    modalContent: {
        background: 'white',
        padding: '32px',
        borderRadius: '16px',
        width: '95%',
        maxWidth: '1000px',
        boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
        border: '1px solid #e2e8f0',
        maxHeight: '90vh',
        overflowY: 'auto'
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px'
    },
    modalActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
        marginTop: '24px'
    },
    formGroup: { marginBottom: '20px' },
    label: {
        display: 'block',
        marginBottom: '8px',
        fontWeight: '600',
        color: '#374151',
        fontSize: '14px'
    },
    input: {
        width: '100%',
        padding: '12px 16px',
        fontSize: '14px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        boxSizing: 'border-box',
        outline: 'none',
        transition: 'border-color 0.2s'
    },
    select: {
        width: '100%',
        padding: '12px 16px',
        fontSize: '14px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        background: 'white',
        outline: 'none',
        transition: 'border-color 0.2s'
    },
    listHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px'
    }
};


function AddProductModal({ onClose, onSave, productGroups, warehouses }) {
    const [isVariantMode, setIsVariantMode] = useState(false);
    const [variantOptions, setVariantOptions] = useState([{ id: 1, name: '', values: [] }]);
    const [generatedVariants, setGeneratedVariants] = useState([]);
    const generatedVariantsRef = useRef(generatedVariants);

    useEffect(() => {
        generatedVariantsRef.current = generatedVariants;
    }, [generatedVariants]);

    const [activeTab, setActiveTab] = useState('details'); // details, variants
    const [autoGenerateBarcode, setAutoGenerateBarcode] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [showConfirmClose, setShowConfirmClose] = useState(false);

    const [newProduct, setNewProduct] = useState({
        name: '',
        barcode: '',
        groupId: '',
        quantity: 0,
        unit: 'Adet',
        criticalLevel: 10,
        salePrice: 0,
        imageUrl: '',
        imageData: null,
        isRawMaterial: false,
        warehouseQuantities: {}
    });

    // Helper to generate variants based on options
    useEffect(() => {
        if (!isVariantMode) return;

        const validOptionsRaw = variantOptions.filter(o => o.name && o.values.length > 0);

        // Merge options with the same name to prevent duplicates and handle multiple values for same option
        const mergedOptionsMap = new Map();
        validOptionsRaw.forEach(opt => {
            const normalizedName = opt.name.trim();
            if (mergedOptionsMap.has(normalizedName)) {
                const existing = mergedOptionsMap.get(normalizedName);
                mergedOptionsMap.set(normalizedName, { ...existing, values: [...new Set([...existing.values, ...opt.values])] });
            } else {
                mergedOptionsMap.set(normalizedName, { ...opt, name: normalizedName });
            }
        });

        const validOptions = Array.from(mergedOptionsMap.values());

        if (validOptions.length === 0) {
            setGeneratedVariants([]);
            return;
        }

        // Generate flat list of variants (one for each value in each option)
        const flatCombinations = [];
        validOptions.forEach(option => {
            option.values.forEach(val => {
                flatCombinations.push({
                    optionName: option.name,
                    value: val,
                    props: { [option.name]: val }
                });
            });
        });

        // Create variant objects
        const variants = flatCombinations.map((item, index) => {
            const fullName = `${newProduct.name || 'Ürün'} - ${item.value}`;

            // Check if this variant already exists in state to preserve custom values
            const existing = generatedVariantsRef.current.find(v =>
                v.variantProperties?.[item.optionName] === item.value && Object.keys(v.variantProperties).length === 1
            );

            if (existing) {
                return {
                    ...existing,
                    name: fullName,
                    barcode: (autoGenerateBarcode && newProduct.barcode) ? `${newProduct.barcode}-${index + 1}` : existing.barcode,
                    imageUrl: existing.imageUrl || newProduct.imageUrl,
                    imageData: existing.imageData || newProduct.imageData
                };
            }

            return {
                ...newProduct,
                barcode: (autoGenerateBarcode && newProduct.barcode) ? `${newProduct.barcode}-${index + 1}` : '',
                name: fullName,
                variantProperties: item.props,
                isVariant: true,
                imageUrl: newProduct.imageUrl || '',
                imageData: newProduct.imageData || null
            };
        });

        setGeneratedVariants(variants);
    }, [isVariantMode, variantOptions, newProduct.name, newProduct.quantity, newProduct.salePrice, newProduct.unit, newProduct.groupId, newProduct.warehouseQuantities, autoGenerateBarcode, newProduct.barcode, newProduct.isRawMaterial, newProduct.imageUrl, newProduct.imageData]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setHasChanges(true);
        if (type === 'checkbox') {
            setNewProduct(prev => ({ ...prev, [name]: checked }));
        } else {
            setNewProduct(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleReleaseVariantOption = (id) => {
        setVariantOptions(prev => prev.filter(o => o.id !== id));
    };

    const handleAddVariantOption = () => {
        setVariantOptions(prev => [...prev, { id: Date.now(), name: '', values: [] }]);
    };

    const handleOptionNameChange = (id, name) => {
        setHasChanges(true);
        setVariantOptions(prev => prev.map(o => o.id === id ? { ...o, name } : o));
    };

    const handleAddOptionValue = (id, value) => {
        if (!value) return;
        setHasChanges(true);
        setVariantOptions(prev => prev.map(o => {
            if (o.id === id && !o.values.includes(value)) {
                return { ...o, values: [...o.values, value] };
            }
            return o;
        }));
    };

    const handleRemoveOptionValue = (id, value) => {
        setVariantOptions(prev => prev.map(o => {
            if (o.id === id) {
                return { ...o, values: o.values.filter(v => v !== value) };
            }
            return o;
        }));
    };

    const handleVariantChange = (index, field, value) => {
        setHasChanges(true);
        setGeneratedVariants(prev => prev.map((v, i) => i === index ? { ...v, [field]: value } : v));
    };

    const handleWarehouseQuantityChange = (warehouseId, quantity) => {
        setHasChanges(true);
        setNewProduct(prev => ({
            ...prev,
            warehouseQuantities: {
                ...prev.warehouseQuantities,
                [warehouseId]: Number(quantity) || 0
            }
        }));
    };

    const handleImageUpload = (imageData) => {
        setHasChanges(true);
        setNewProduct(prev => ({
            ...prev,
            imageUrl: imageData ? imageData.thumbnailUrl : '',
            imageData: imageData
        }));
    };

    const handleSubmit = (e) => {
        if (e) e.preventDefault();

        if (isVariantMode) {
            if (generatedVariants.length === 0) {
                alert('Lütfen en az bir varyant seçeneği ekleyin.');
                return;
            }
            const variantGroupId = doc(collection(db, 'temp')).id;
            const variantsToSave = generatedVariants.map(v => ({
                ...v,
                variantGroupId,
                quantity: Object.values(v.warehouseQuantities || {}).reduce((sum, qty) => sum + qty, v.quantity || 0)
            }));

            const mainProductToSave = {
                ...newProduct,
                variantGroupId,
                quantity: Object.values(newProduct.warehouseQuantities || {}).reduce((sum, qty) => sum + qty, newProduct.quantity || 0)
            };

            onSave([...variantsToSave, mainProductToSave]);
        } else {
            const totalQuantity = Object.values(newProduct.warehouseQuantities).reduce((sum, qty) => sum + qty, 0);
            onSave({ ...newProduct, quantity: totalQuantity });
        }
        setHasChanges(false);
    };

    const handleCloseRequest = () => {
        if (hasChanges) {
            setShowConfirmClose(true);
        } else {
            onClose();
        }
    };

    return (
        <div style={styles.modalOverlay} onClick={handleCloseRequest}>
            <div style={{ ...styles.modalContent, maxWidth: '1000px' }} onClick={e => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <h2 style={styles.title}>Yeni Ürün Ekle</h2>
                    <button onClick={handleCloseRequest} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>&times;</button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div style={{ display: 'flex', gap: '20px', marginBottom: '20px', borderBottom: '1px solid #e2e8f0' }}>
                        <button
                            type="button"
                            onClick={() => setActiveTab('details')}
                            style={{
                                padding: '10px 0',
                                border: 'none',
                                background: 'transparent',
                                borderBottom: activeTab === 'details' ? '2px solid #06C167' : 'none',
                                color: activeTab === 'details' ? '#06C167' : '#64748b',
                                fontWeight: '600',
                                cursor: 'pointer'
                            }}
                        >
                            Ürün Detayları
                        </button>
                        {isVariantMode && (
                            <button
                                type="button"
                                onClick={() => setActiveTab('variants')}
                                style={{
                                    padding: '10px 0',
                                    border: 'none',
                                    background: 'transparent',
                                    borderBottom: activeTab === 'variants' ? '2px solid #06C167' : 'none',
                                    color: activeTab === 'variants' ? '#06C167' : '#64748b',
                                    fontWeight: '600',
                                    cursor: 'pointer'
                                }}
                            >
                                Varyantlar ({generatedVariants.length})
                            </button>
                        )}
                    </div>

                    <div style={{ display: activeTab === 'details' ? 'block' : 'none' }}>
                        <div style={styles.formGroup}><label style={styles.label}>Ürün Adı</label><input style={styles.input} type="text" name="name" value={newProduct.name} onChange={handleChange} required placeholder="Örn: Pamuklu Tişört" /></div>

                        <div style={{ display: 'flex', gap: '20px' }}>
                            <div style={{ ...styles.formGroup, flex: 1 }}><label style={styles.label}>Ürün Grubu</label><select style={styles.select} name="groupId" value={newProduct.groupId} onChange={handleChange} required><option value="">Grup Seçin</option>{productGroups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}</select></div>
                            <div style={{ ...styles.formGroup, flex: 1 }}><label style={styles.label}>Birim</label>
                                <select style={styles.select} name="unit" value={newProduct.unit} onChange={handleChange}>
                                    <option>Adet</option>
                                    <option>Kg</option>
                                    <option>Litre</option>
                                    <option>Metre</option>
                                </select>
                            </div>
                        </div>

                        <div style={{ ...styles.formGroup, background: '#f8fafc', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                            <label style={{ ...styles.label, display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    checked={isVariantMode}
                                    onChange={(e) => {
                                        setIsVariantMode(e.target.checked);
                                        if (e.target.checked) setActiveTab('details');
                                    }}
                                />
                                Bu ürünün varyantları var (Renk, Beden vb.)
                            </label>
                            {isVariantMode && (
                                <div style={{ marginTop: '16px' }}>
                                    {variantOptions.map((option, index) => (
                                        <div key={option.id} style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid #e2e8f0' }}>
                                            <div style={{ display: 'flex', gap: '10px', marginBottom: '8px' }}>
                                                <input
                                                    style={{ ...styles.input, width: '150px' }}
                                                    placeholder="Seçenek Adı (Örn: Beden)"
                                                    value={option.name}
                                                    onChange={(e) => handleOptionNameChange(option.id, e.target.value)}
                                                />
                                                <input
                                                    style={{ ...styles.input, flex: 1 }}
                                                    placeholder="Değer ekleyip Enter'a basın (S, M, L...)"
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            handleAddOptionValue(option.id, e.target.value);
                                                            e.target.value = '';
                                                        }
                                                    }}
                                                />
                                                {variantOptions.length > 1 && (
                                                    <button type="button" onClick={() => handleReleaseVariantOption(option.id)} style={{ color: '#ef4444', border: 'none', background: 'none', cursor: 'pointer' }}>Sil</button>
                                                )}
                                            </div>
                                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                                {option.values.map(val => (
                                                    <span key={val} style={{ background: '#e0e7ff', color: '#4338ca', padding: '2px 8px', borderRadius: '4px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                        {val}
                                                        <button type="button" onClick={() => handleRemoveOptionValue(option.id, val)} style={{ border: 'none', background: 'none', color: '#4338ca', cursor: 'pointer', padding: 0, fontWeight: 'bold' }}>&times;</button>
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                    <button type="button" onClick={handleAddVariantOption} style={{ ...styles.buttonSecondary, fontSize: '12px', padding: '6px 12px' }}>+ Seçenek Ekle</button>

                                    <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px dashed #e2e8f0' }}>
                                        <label style={{ ...styles.label, display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: '#475569' }}>
                                            <input
                                                type="checkbox"
                                                checked={autoGenerateBarcode}
                                                onChange={(e) => setAutoGenerateBarcode(e.target.checked)}
                                            />
                                            Varyantlar için otomatik barkod oluştur (Ana Barkod-1, Ana Barkod-2...)
                                        </label>
                                    </div>
                                </div>
                            )}
                        </div>

                        {isVariantMode && (
                            <div style={styles.formGroup}><label style={styles.label}>Ana Barkod (Otomatik üretim için ön ek)</label><input style={styles.input} type="text" name="barcode" value={newProduct.barcode} onChange={handleChange} placeholder="Örn: 12345" /></div>
                        )}

                        {!isVariantMode && (
                            <>
                                <div style={styles.formGroup}><label style={styles.label}>Barkod</label><input style={styles.input} type="text" name="barcode" value={newProduct.barcode} onChange={handleChange} /></div>
                                {/* Warehouse-based stock entry */}
                                {warehouses.length > 0 ? (
                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>Depo Bazlı Stok Miktarı</label>
                                        {warehouses.filter(w => w.isActive).map(warehouse => (
                                            <div key={warehouse.id} style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                                                <div style={{ flex: 1, fontSize: '14px', display: 'flex', alignItems: 'center' }}>{warehouse.name}</div>
                                                <input
                                                    style={{ ...styles.input, flex: 1 }}
                                                    type="number"
                                                    value={newProduct.warehouseQuantities[warehouse.id] || 0}
                                                    onChange={(e) => handleWarehouseQuantityChange(warehouse.id, e.target.value)}
                                                    min="0"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div style={styles.formGroup}><label style={styles.label}>Stok Miktarı</label><input style={styles.input} type="number" name="quantity" value={newProduct.quantity} onChange={handleChange} /></div>
                                )}
                            </>
                        )}

                        <div style={{ display: 'flex', gap: '20px' }}>
                            <div style={{ ...styles.formGroup, flex: 1 }}><label style={styles.label}>Kritik Stok Seviyesi</label><input style={styles.input} type="number" name="criticalLevel" value={newProduct.criticalLevel} onChange={handleChange} /></div>
                            <div style={{ ...styles.formGroup, flex: 1 }}><label style={styles.label}>Satış Fiyatı ({!isVariantMode ? '₺' : 'Varsayılan ₺'})</label><input style={styles.input} type="number" name="salePrice" value={newProduct.salePrice} onChange={handleChange} /></div>
                        </div>

                        <div style={styles.formGroup}>
                            <label style={{ ...styles.label, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <input
                                    type="checkbox"
                                    name="isRawMaterial"
                                    checked={newProduct.isRawMaterial}
                                    onChange={handleChange}
                                />
                                Hammadde olarak kullan
                            </label>
                            {newProduct.isRawMaterial && (
                                <div style={{ fontSize: '12px', color: '#e67700', marginTop: '4px', fontWeight: '500' }}>
                                    ⚠️ Bu ürün hammadde listesinde görünecek
                                </div>
                            )}
                        </div>

                        <div style={styles.formGroup}>
                            <ImageUpload
                                onImageUpload={handleImageUpload}
                                currentImageUrl={newProduct.imageUrl}
                                label="Ürün Görseli"
                            />
                        </div>
                    </div>

                    {isVariantMode && (
                        <div style={{ display: activeTab === 'variants' ? 'block' : 'none' }}>
                            <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                                <table style={styles.table}>
                                    <thead style={{ position: 'sticky', top: 0, background: '#f8fafc', zIndex: 1 }}>
                                        <tr>
                                            <th style={styles.th}>Görsel</th>
                                            <th style={styles.th}>Ürün Adı</th>
                                            <th style={styles.th}>Varyant Tipi</th>
                                            <th style={styles.th}>Değer</th>
                                            <th style={styles.th}>Barkod</th>
                                            <th style={styles.th}>Fiyat</th>
                                            <th style={styles.th}>Stok</th>
                                            <th style={styles.th}>Hammadde</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {generatedVariants.map((variant, index) => (
                                            <tr key={index}>
                                                <td style={styles.td}>
                                                    <div style={{ width: '80px' }}>
                                                        <ImageUpload
                                                            onImageUpload={(imgData) => {
                                                                handleVariantChange(index, 'imageUrl', imgData ? imgData.thumbnailUrl : '');
                                                                handleVariantChange(index, 'imageData', imgData);
                                                            }}
                                                            currentImageUrl={variant.imageUrl}
                                                        />
                                                    </div>
                                                </td>
                                                <td style={styles.td}>
                                                    <input
                                                        style={{ ...styles.input, padding: '6px' }}
                                                        value={variant.name || ''}
                                                        onChange={(e) => handleVariantChange(index, 'name', e.target.value)}
                                                        placeholder="Ürün Adı"
                                                    />
                                                </td>
                                                <td style={styles.td}>
                                                    {Object.keys(variant.variantProperties || {})[0] || '-'}
                                                </td>
                                                <td style={styles.td}>
                                                    {Object.values(variant.variantProperties || {})[0] || '-'}
                                                </td>
                                                <td style={styles.td}>
                                                    <input
                                                        style={{ ...styles.input, padding: '6px' }}
                                                        value={variant.barcode || ''}
                                                        onChange={(e) => handleVariantChange(index, 'barcode', e.target.value)}
                                                        placeholder="Barkod"
                                                    />
                                                </td>
                                                <td style={styles.td}>
                                                    <input
                                                        style={{ ...styles.input, padding: '6px', width: '80px' }}
                                                        type="number"
                                                        value={variant.salePrice}
                                                        onChange={(e) => handleVariantChange(index, 'salePrice', Number(e.target.value))}
                                                    />
                                                </td>
                                                <td style={styles.td}>
                                                    <input
                                                        style={{ ...styles.input, padding: '6px', width: '60px' }}
                                                        type="number"
                                                        value={variant.quantity}
                                                        onChange={(e) => handleVariantChange(index, 'quantity', Number(e.target.value))}
                                                        title="Şu an için varsayılan depoya eklenecektir"
                                                    />
                                                </td>
                                                <td style={{ ...styles.td, textAlign: 'center' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={variant.isRawMaterial}
                                                        onChange={(e) => handleVariantChange(index, 'isRawMaterial', e.target.checked)}
                                                        title="Hammadde olarak işaretle"
                                                        style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                        {generatedVariants.length === 0 && (
                                            <tr>
                                                <td colSpan="8" style={{ padding: '20px', textAlign: 'center', color: '#64748b' }}>
                                                    Lütfen önce seçenekleri ve değerlerini belirleyin.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    <div style={styles.modalActions}>
                        <button type="button" onClick={handleCloseRequest} style={{ ...styles.button, background: '#6c757d' }}>İptal</button>
                        <button type="submit" style={{ ...styles.button, background: '#198754' }}>
                            {isVariantMode ? `${generatedVariants.length} Ürünü Kaydet` : 'Kaydet'}
                        </button>
                    </div>
                </form>

                {showConfirmClose && (
                    <div style={{ ...styles.modalOverlay, background: 'rgba(0,0,0,0.6)', zIndex: 110 }}>
                        <div style={{ ...styles.modalContent, maxWidth: '400px', padding: '24px', textAlign: 'center' }}>
                            <h3 style={{ marginBottom: '16px' }}>Kaydedilmemiş Değişiklikler</h3>
                            <p style={{ color: '#64748b', marginBottom: '24px' }}>Kaydedilmemiş değişiklikleriniz var. Ne yapmak istersiniz?</p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                <button
                                    onClick={() => handleSubmit()}
                                    style={{ ...styles.button, background: '#198754', width: '100%', cursor: 'pointer' }}
                                >
                                    Kaydet ve Çık
                                </button>
                                <button
                                    onClick={onClose}
                                    style={{ ...styles.button, background: '#dc3545', width: '100%', cursor: 'pointer' }}
                                >
                                    Kaydetmeden Çık
                                </button>
                                <button
                                    onClick={() => setShowConfirmClose(false)}
                                    style={{ ...styles.button, background: '#f8fafc', color: '#1e293b', border: '1px solid #e2e8f0', width: '100%', cursor: 'pointer' }}
                                >
                                    Vazgeç (Panelde Kal)
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}


function ExcelImportModal({ onClose, onImport, onDownloadTemplate, isImporting }) {
    const [excelFile, setExcelFile] = useState(null);

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <h2 style={styles.title}>Excel ile İçeri Aktar</h2>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>&times;</button>
                </div>
                <div style={styles.formGroup}>
                    <label style={styles.label}>Stok Dosyası (.xlsx)</label>
                    <input type="file" accept=".xlsx, .xls" onChange={(e) => setExcelFile(e.target.files[0])} style={styles.input} />
                </div>
                <div style={styles.modalActions}>
                    <button onClick={onDownloadTemplate} style={{ ...styles.button, background: 'transparent', color: '#06C167', border: '1px solid #06C167', marginRight: 'auto' }}>Şablon İndir</button>
                    <button onClick={onClose} style={{ ...styles.button, background: '#6c757d' }}>İptal</button>
                    <button onClick={() => onImport(excelFile)} disabled={isImporting || !excelFile} style={{ ...styles.button, background: '#198754' }}>
                        {isImporting ? 'Aktarılıyor...' : 'İçeri Aktar'}
                    </button>
                </div>
            </div>
        </div>
    );
}



function ExportOptionsModal({ onClose, onExport, selectedCount }) {
    const [format, setFormat] = useState('excel'); // excel, pdf
    const [showPrice, setShowPrice] = useState(true);

    const handleExport = () => {
        onExport({ format, showPrice });
    };

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={{ ...styles.modalContent, maxWidth: '400px' }} onClick={e => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <h2 style={styles.title}>Dışa Aktarma Seçenekleri</h2>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>&times;</button>
                </div>

                <div style={{ padding: '20px', background: '#f8fafc', borderRadius: '8px', marginBottom: '20px' }}>
                    <p style={{ marginBottom: '16px', color: '#64748b', fontSize: '14px' }}>
                        Seçili <strong>{selectedCount}</strong> ürün için dışa aktarma formatını belirleyin.
                    </p>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Dosya Formatı</label>
                        <div style={{ display: 'flex', gap: '16px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                <input
                                    type="radio"
                                    name="exportFormat"
                                    value="excel"
                                    checked={format === 'excel'}
                                    onChange={(e) => setFormat(e.target.value)}
                                />
                                Excel (.xlsx)
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                <input
                                    type="radio"
                                    name="exportFormat"
                                    value="pdf"
                                    checked={format === 'pdf'}
                                    onChange={(e) => setFormat(e.target.value)}
                                />
                                PDF (.pdf)
                            </label>
                        </div>
                    </div>

                    <div style={{ ...styles.formGroup, marginBottom: 0 }}>
                        <label style={{ ...styles.label, display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: 0 }}>
                            <input
                                type="checkbox"
                                checked={showPrice}
                                onChange={(e) => setShowPrice(e.target.checked)}
                            />
                            Satış Fiyatı Gösterilsin
                        </label>
                    </div>
                </div>

                <div style={styles.modalActions}>
                    <button onClick={onClose} style={{ ...styles.button, background: '#6c757d' }}>İptal</button>
                    <button onClick={handleExport} style={{ ...styles.button, background: '#059669' }}>
                        {format === 'excel' ? '📥 Excel Olarak İndir' : '📥 PDF Olarak İndir'}
                    </button>
                </div>
            </div>
        </div>
    );
}

function BulkPriceUpdateModal({ onClose, onUpdate, selectedCount }) {
    const [updateType, setUpdateType] = useState('percentage'); // percentage, fixed, set
    const [value, setValue] = useState(0);
    const [operation, setOperation] = useState('increase'); // increase, decrease

    const handleSubmit = (e) => {
        e.preventDefault();
        onUpdate({ updateType, value: Number(value), operation });
    };

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <h2 style={styles.title}>Toplu Fiyat Güncelle ({selectedCount} Ürün)</h2>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>&times;</button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Güncelleme Türü</label>
                        <select
                            style={styles.select}
                            value={updateType}
                            onChange={(e) => setUpdateType(e.target.value)}
                        >
                            <option value="percentage">Yüzde (%) Değişim</option>
                            <option value="fixed">Sabit Tutar (₺) Değişim</option>
                            <option value="set">Yeni Fiyat Belirle (₺)</option>
                        </select>
                    </div>

                    {updateType !== 'set' && (
                        <div style={styles.formGroup}>
                            <label style={styles.label}>İşlem</label>
                            <div style={{ display: 'flex', gap: '20px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                    <input
                                        type="radio"
                                        name="operation"
                                        value="increase"
                                        checked={operation === 'increase'}
                                        onChange={(e) => setOperation(e.target.value)}
                                    />
                                    Artır (+)
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                    <input
                                        type="radio"
                                        name="operation"
                                        value="decrease"
                                        checked={operation === 'decrease'}
                                        onChange={(e) => setOperation(e.target.value)}
                                    />
                                    Azalt (-)
                                </label>
                            </div>
                        </div>
                    )}

                    <div style={styles.formGroup}>
                        <label style={styles.label}>
                            {updateType === 'percentage' ? 'Yüzde Oranı (%)' :
                                updateType === 'fixed' ? 'Değişim Tutarı (₺)' :
                                    'Yeni Fiyat (₺)'}
                        </label>
                        <input
                            style={styles.input}
                            type="number"
                            step="0.01"
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            required
                        />
                    </div>

                    <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '8px', marginBottom: '20px', fontSize: '13px', color: '#64748b' }}>
                        {updateType === 'percentage' && (
                            <p>Seçili {selectedCount} ürünün fiyatı {operation === 'increase' ? '% ' + value + ' zamlanacak.' : '% ' + value + ' indirilecek.'}</p>
                        )}
                        {updateType === 'fixed' && (
                            <p>Seçili {selectedCount} ürünün fiyatı {operation === 'increase' ? value + ' ₺ artırılacak.' : value + ' ₺ azaltılacak.'}</p>
                        )}
                        {updateType === 'set' && (
                            <p>Seçili {selectedCount} ürünün tamamının fiyatı {value} ₺ olarak güncellenecek.</p>
                        )}
                    </div>

                    <div style={styles.modalActions}>
                        <button type="button" onClick={onClose} style={{ ...styles.button, background: '#6c757d' }}>İptal</button>
                        <button type="submit" style={{ ...styles.button, background: '#06C167' }}>Fiyatları Güncelle</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function BulkStockUpdateModal({ onClose, onUpdate, warehouses, selectedCount }) {
    const [operation, setOperation] = useState('increase'); // increase, decrease, set
    const [amount, setAmount] = useState(0);
    const [selectedWarehouseId, setSelectedWarehouseId] = useState(warehouses.find(w => w.isDefault)?.id || warehouses[0]?.id || '');
    const [note, setNote] = useState('Toplu Stok Güncelleme');

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!selectedWarehouseId) {
            alert('Lütfen bir depo seçin.');
            return;
        }
        onUpdate({ operation, amount: Number(amount), warehouseId: selectedWarehouseId, note });
    };

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <h2 style={styles.title}>Toplu Stok Güncelle ({selectedCount} Ürün)</h2>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>&times;</button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>İşlem Türü</label>
                        <div style={{ display: 'flex', gap: '15px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                <input
                                    type="radio"
                                    name="stockOp"
                                    value="increase"
                                    checked={operation === 'increase'}
                                    onChange={(e) => setOperation(e.target.value)}
                                />
                                Artır (+)
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                <input
                                    type="radio"
                                    name="stockOp"
                                    value="decrease"
                                    checked={operation === 'decrease'}
                                    onChange={(e) => setOperation(e.target.value)}
                                />
                                Azalt (-)
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                <input
                                    type="radio"
                                    name="stockOp"
                                    value="set"
                                    checked={operation === 'set'}
                                    onChange={(e) => setOperation(e.target.value)}
                                />
                                Eşitle (=)
                            </label>
                        </div>
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Hedef Depo</label>
                        <select
                            style={styles.select}
                            value={selectedWarehouseId}
                            onChange={(e) => setSelectedWarehouseId(e.target.value)}
                            required
                        >
                            <option value="">Depo Seçin...</option>
                            {warehouses.filter(w => w.isActive).map(w => (
                                <option key={w.id} value={w.id}>{w.name}</option>
                            ))}
                        </select>
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Miktar/Adet</label>
                        <input
                            style={styles.input}
                            type="number"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            required
                            min="0"
                            step="0.01"
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Açıklama</label>
                        <input
                            style={styles.input}
                            type="text"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Örn: Stok sayımı sonrası düzeltme"
                        />
                    </div>

                    <div style={{ padding: '16px', background: '#f0fdf4', borderRadius: '8px', marginBottom: '20px', fontSize: '13px', color: '#166534' }}>
                        {operation === 'increase' && <p>Seçili {selectedCount} ürünün stokuna {amount} adet <strong>eklenecek</strong>.</p>}
                        {operation === 'decrease' && <p>Seçili {selectedCount} ürünün stokundan {amount} adet <strong>düşülecek</strong>.</p>}
                        {operation === 'set' && <p>Seçili {selectedCount} ürünün stoku {amount} adet olarak <strong>güncellenecek</strong>.</p>}
                        <p style={{ marginTop: '4px', fontStyle: 'italic' }}>* Seçilen depo: {warehouses.find(w => w.id === selectedWarehouseId)?.name}</p>
                    </div>

                    <div style={styles.modalActions}>
                        <button type="button" onClick={onClose} style={{ ...styles.button, background: '#6c757d' }}>İptal</button>
                        <button type="submit" style={{ ...styles.button, background: '#10b981' }}>Stokları Güncelle</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// Modal for transferring stock between warehouses
function TransferStockModal({ onClose, onTransfer, warehouses, products, selectedProductIds }) {
    const [fromWarehouseId, setFromWarehouseId] = useState('');
    const [toWarehouseId, setToWarehouseId] = useState('');
    const [amount, setAmount] = useState(0);
    const [note, setNote] = useState('Depolar Arası Transfer');

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!fromWarehouseId || !toWarehouseId) {
            alert('Lütfen kaynak ve hedef depoları seçin.');
            return;
        }
        if (fromWarehouseId === toWarehouseId) {
            alert('Kaynak ve hedef depo aynı olamaz.');
            return;
        }
        if (Number(amount) <= 0) {
            alert('Lütfen geçerli bir miktar girin.');
            return;
        }
        onTransfer({ fromWarehouseId, toWarehouseId, amount: Number(amount), note });
    };

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <h2 style={styles.title}>Depolar Arası Transfer ({selectedProductIds.length} Ürün)</h2>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>&times;</button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Kaynak Depo (Çıkış)</label>
                        <select
                            style={styles.select}
                            value={fromWarehouseId}
                            onChange={(e) => setFromWarehouseId(e.target.value)}
                            required
                        >
                            <option value="">Depo Seçin...</option>
                            {warehouses.filter(w => w.isActive).map(w => (
                                <option key={w.id} value={w.id}>{w.name}</option>
                            ))}
                        </select>
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Hedef Depo (Giriş)</label>
                        <select
                            style={styles.select}
                            value={toWarehouseId}
                            onChange={(e) => setToWarehouseId(e.target.value)}
                            required
                        >
                            <option value="">Depo Seçin...</option>
                            {warehouses.filter(w => w.isActive && w.id !== fromWarehouseId).map(w => (
                                <option key={w.id} value={w.id}>{w.name}</option>
                            ))}
                        </select>
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Transfer Miktarı</label>
                        <input
                            style={styles.input}
                            type="number"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            required
                            min="1"
                            step="1"
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Açıklama</label>
                        <input
                            style={styles.input}
                            type="text"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Örn: Sevkiyat için transfer"
                        />
                    </div>

                    <div style={{ padding: '16px', background: '#eff6ff', borderRadius: '8px', marginBottom: '20px', fontSize: '13px', color: '#1e40af' }}>
                        <p>Seçili <strong>{selectedProductIds.length}</strong> ürünün her biri için:</p>
                        <p>• <strong>{warehouses.find(w => w.id === fromWarehouseId)?.name || '...'}</strong> deposundan <strong>{amount || 0}</strong> adet çıkış yapılacak</p>
                        <p>• <strong>{warehouses.find(w => w.id === toWarehouseId)?.name || '...'}</strong> deposuna <strong>{amount || 0}</strong> adet giriş yapılacak</p>
                    </div>

                    <div style={styles.modalActions}>
                        <button type="button" onClick={onClose} style={{ ...styles.button, background: '#6c757d' }}>İptal</button>
                        <button type="submit" style={{ ...styles.button, background: '#3b82f6' }}>🔄 Transfer Et</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function StoreTransferModal({ onClose, onTransfer, warehouses, stores, products, selectedProductIds }) {
    const [fromWarehouseId, setFromWarehouseId] = useState('');
    const [toStoreId, setToStoreId] = useState('');
    const [amount, setAmount] = useState(0);
    const [note, setNote] = useState('Mağazaya Sevk');

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!fromWarehouseId || !toStoreId) {
            alert('Lütfen kaynak depo ve hedef mağazayı seçin.');
            return;
        }
        if (Number(amount) <= 0) {
            alert('Lütfen geçerli bir miktar girin.');
            return;
        }
        onTransfer({ fromWarehouseId, toStoreId, amount: Number(amount), note });
    };

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <h2 style={styles.title}>Mağazaya Sevk Et ({selectedProductIds.length} Ürün)</h2>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>&times;</button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Kaynak Depo (Çıkış)</label>
                        <select
                            style={styles.select}
                            value={fromWarehouseId}
                            onChange={(e) => setFromWarehouseId(e.target.value)}
                            required
                        >
                            <option value="">Depo Seçin...</option>
                            {warehouses.filter(w => w.isActive).map(w => (
                                <option key={w.id} value={w.id}>{w.name}</option>
                            ))}
                        </select>
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Hedef Mağaza (Giriş)</label>
                        <select
                            style={styles.select}
                            value={toStoreId}
                            onChange={(e) => setToStoreId(e.target.value)}
                            required
                        >
                            <option value="">Mağaza Seçin...</option>
                            {stores.filter(s => s.status !== 'inactive').map(s => (
                                <option key={s.id} value={s.id}>{s.name || s.storeName}</option>
                            ))}
                        </select>
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Transfer Miktarı (Her Ürün İçin)</label>
                        <input
                            style={styles.input}
                            type="number"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            required
                            min="1"
                            step="1"
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Açıklama</label>
                        <input
                            style={styles.input}
                            type="text"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Örn: Mağaza stok ikmali"
                        />
                    </div>

                    <div style={{ padding: '16px', background: '#eff6ff', borderRadius: '8px', marginBottom: '20px', fontSize: '13px', color: '#1e40af' }}>
                        <p>Seçili <strong>{selectedProductIds.length}</strong> ürünün her biri için:</p>
                        <p>• <strong>{warehouses.find(w => w.id === fromWarehouseId)?.name || '...'}</strong> deposundan çıkış yapılacak</p>
                        <p>• <strong>{stores.find(s => s.id === toStoreId)?.name || '...'}</strong> mağaza envanterine <strong>{amount || 0}</strong> adet eklenecek</p>
                    </div>

                    <div style={styles.modalActions}>
                        <button type="button" onClick={onClose} style={{ ...styles.button, background: '#6c757d' }}>İptal</button>
                        <button type="submit" style={{ ...styles.button, background: '#8b5cf6' }}>🚀 Sevk Et</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function Stock() {
    const { userData, canViewPrices } = useAuth();
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [productGroups, setProductGroups] = useState([]);
    const [warehouses, setWarehouses] = useState([]); // Add warehouses state
    const [stores, setStores] = useState([]); // Add stores state
    const [recentMovements, setRecentMovements] = useState([]); // Add recent movements state
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'ascending' });
    const [showAddModal, setShowAddModal] = useState(false);
    const [showExcelModal, setShowExcelModal] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [showRawMaterialsOnly, setShowRawMaterialsOnly] = useState(false);
    const [printProduct, setPrintProduct] = useState(null);
    const [selectedProductIds, setSelectedProductIds] = useState([]);
    const [printProducts, setPrintProducts] = useState(null); // For bulk printing
    const [showBulkPriceModal, setShowBulkPriceModal] = useState(false);
    const [showBulkStockModal, setShowBulkStockModal] = useState(false);
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [showStoreTransferModal, setShowStoreTransferModal] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);

    const downloadTemplate = () => {
        // Create template data
        const templateData = [
            {
                urun_adi: 'Örnek Ürün Adı',
                barkod: '123456789',
                grup_adi: productGroups.length > 0 ? productGroups[0].name : 'Genel',
                miktar: 100,
                birim: 'Adet',
                satis_fiyati: 25.99,
                kritik_seviye: 10
            }
        ];

        // Create worksheet
        const worksheet = XLSX.utils.json_to_sheet(templateData);

        // Create workbook
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Stok_Sablonu');

        // Download the file
        XLSX.writeFile(workbook, 'stok_yukleme_sablonu.xlsx');
    };

    useEffect(() => {
        if (!userData?.companyId) {
            setLoading(false);
            return;
        }
        const companyId = userData.companyId;
        const unsubGroups = onSnapshot(query(collection(db, 'companies', companyId, 'productGroups')), snap => setProductGroups(snap.docs.map(d => ({ id: d.id, ...d.data() }))));

        // Load warehouses
        const unsubWarehouses = onSnapshot(query(collection(db, 'companies', companyId, 'warehouses')), snap => setWarehouses(snap.docs.map(d => ({ id: d.id, ...d.data() }))));

        // Load stores
        const unsubStores = onSnapshot(query(collection(db, 'companies', companyId, 'stores')), snap => setStores(snap.docs.map(d => ({ id: d.id, ...d.data() }))));

        const unsubProducts = onSnapshot(query(collection(db, 'companies', companyId, 'products')), snap => {
            setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            setLoading(false);
        });

        // Load recent movements from all products
        const loadRecentMovements = async () => {
            try {
                // Get all products first
                const productsSnapshot = await getDocs(collection(db, 'companies', companyId, 'products'));
                const allMovements = [];

                // For each product, get its recent movements
                for (const productDoc of productsSnapshot.docs) {
                    const productData = productDoc.data();
                    const movementsQuery = query(
                        collection(db, 'companies', companyId, 'products', productDoc.id, 'movements'),
                        orderBy('date', 'desc'),
                        // Limit to 3 most recent movements per product
                    );
                    const movementsSnapshot = await getDocs(movementsQuery);

                    movementsSnapshot.docs.slice(0, 3).forEach(movementDoc => {
                        allMovements.push({
                            id: movementDoc.id,
                            productId: productDoc.id,
                            productName: productData.name,
                            productBarcode: productData.barcode,
                            ...movementDoc.data()
                        });
                    });
                }

                // Sort all movements by date and take top 10
                const sortedMovements = allMovements
                    .sort((a, b) => {
                        const dateA = a.date?.seconds || 0;
                        const dateB = b.date?.seconds || 0;
                        return dateB - dateA;
                    })
                    .slice(0, 10);

                setRecentMovements(sortedMovements);
            } catch (error) {
                console.error("Son hareketler yüklenirken hata:", error);
            }
        };

        // Load recent movements initially
        loadRecentMovements();

        // Set up periodic refresh (every 30 seconds)
        const interval = setInterval(loadRecentMovements, 30000);

        return () => {
            unsubGroups();
            unsubProducts();
            unsubWarehouses();
            unsubStores();
            clearInterval(interval);
        };
    }, [userData]);

    const handleAddProduct = async (newProductDataOrArray) => {
        if (!userData?.companyId) return;

        const productsToAdd = Array.isArray(newProductDataOrArray) ? newProductDataOrArray : [newProductDataOrArray];

        // Basic validation - check if first item has required fields
        if (!productsToAdd[0]?.name || !productsToAdd[0]?.groupId) return;

        const companyId = userData.companyId;
        const batch = writeBatch(db);

        for (const newProductData of productsToAdd) {
            const productRef = doc(collection(db, 'companies', companyId, 'products'));

            // Prepare warehouse quantities and total quantity
            let warehouseQuantities = newProductData.warehouseQuantities || {};
            let totalQuantity = Number(newProductData.quantity) || 0;

            // Critical fix: If quantity exists but no warehouse assignment (common for variants or quick add), 
            // assign to default warehouse immediately BEFORE saving
            if (Object.keys(warehouseQuantities).length === 0 && totalQuantity > 0 && warehouses.length > 0) {
                const defaultWarehouse = warehouses.find(w => w.isActive && w.isDefault) || warehouses.find(w => w.isActive);
                if (defaultWarehouse) {
                    warehouseQuantities = { [defaultWarehouse.id]: totalQuantity };
                    // Recalculate total quantity from warehouse distribution to ensure consistency
                    totalQuantity = Object.values(warehouseQuantities).reduce((sum, qty) => sum + Number(qty), 0);
                }
            } else if (Object.keys(warehouseQuantities).length > 0) {
                // If warehouse quantities provided, ensure total matches
                totalQuantity = Object.values(warehouseQuantities).reduce((sum, qty) => sum + Number(qty), 0);
            }

            batch.set(productRef, {
                ...newProductData,
                quantity: Number(totalQuantity),
                criticalLevel: Number(newProductData.criticalLevel),
                salePrice: Number(newProductData.salePrice),
                imageUrl: newProductData.imageUrl || '',
                imageData: newProductData.imageData || null,
                warehouseQuantities: warehouseQuantities, // Save the populated map
                createdAt: new Date(),
            });

            // Create movement records
            if (Object.keys(warehouseQuantities).length > 0) {
                for (const [warehouseId, quantity] of Object.entries(warehouseQuantities)) {
                    if (Number(quantity) > 0) {
                        const warehouse = warehouses.find(w => w.id === warehouseId);
                        const movementRef = doc(collection(productRef, 'movements'));
                        batch.set(movementRef, {
                            date: new Date(),
                            type: `Manuel Giriş (İlk Kayıt)`,
                            quantity: `+${quantity} ${newProductData.unit}`,
                            warehouseId: warehouseId,
                            warehouseName: warehouse?.name || 'Depo',
                            description: 'Yeni ürün kaydı ile açılış stoğu'
                        });
                    }
                }
            } else if (totalQuantity > 0) {
                // Fallback for no warehouses
                const movementRef = doc(collection(productRef, 'movements'));
                batch.set(movementRef, {
                    date: new Date(),
                    type: 'Manuel Giriş (İlk Kayıt)',
                    quantity: `+${totalQuantity} ${newProductData.unit}`,
                    description: 'Yeni ürün kaydı ile açılış stoğu'
                });
            }
        }

        try {
            await batch.commit();
            setShowAddModal(false);
            alert(productsToAdd.length > 1 ? `${productsToAdd.length} ürün başarıyla oluşturuldu!` : 'Ürün başarıyla oluşturuldu!');
        } catch (error) {
            console.error("Ürün eklenirken hata:", error);
            alert("Ürünler eklenirken bir hata oluştu.");
        }
    };

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const toggleProductSelection = (id) => {
        setSelectedProductIds(prev =>
            prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
        );
    };

    const toggleAllSelection = () => {
        if (selectedProductIds.length === sortedProducts.length) {
            setSelectedProductIds([]);
        } else {
            setSelectedProductIds(sortedProducts.map(p => p.id));
        }
    };

    const sortedProducts = useMemo(() => {
        let sortableProducts = [...products].filter(product =>
            product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (product.barcode && product.barcode.toLowerCase().includes(searchTerm.toLowerCase()))
        );

        // Filter for raw materials if the toggle is on
        if (showRawMaterialsOnly) {
            sortableProducts = sortableProducts.filter(product => product.isRawMaterial);
        }

        if (sortConfig.key !== null) {
            sortableProducts.sort((a, b) => {
                if (a[sortConfig.key] < b[sortConfig.key]) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (a[sortConfig.key] > b[sortConfig.key]) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableProducts;
    }, [products, searchTerm, sortConfig, showRawMaterialsOnly]);

    const getSortIndicator = (key) => {
        if (sortConfig.key === key) {
            return sortConfig.direction === 'ascending' ? ' 🔼' : ' 🔽';
        }
        return null;
    };

    const handleImport = async (excelFile) => {
        if (!excelFile) {
            alert("Lütfen önce bir Excel dosyası seçin.");
            return;
        }
        setIsImporting(true);

        const reader = new FileReader();
        reader.onload = async (e) => {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const json = XLSX.utils.sheet_to_json(worksheet);

            const companyId = userData.companyId;
            const productsRef = collection(db, 'companies', companyId, 'products');
            const batch = writeBatch(db);

            for (const item of json) {
                if (!item.barkod) continue;

                const q = query(productsRef, where("barcode", "==", String(item.barkod)));
                const existingProductSnap = await getDocs(q);

                const group = productGroups.find(g => g.name === item.grup_adi);

                if (!existingProductSnap.empty) {
                    const productDoc = existingProductSnap.docs[0];
                    const newQuantity = productDoc.data().quantity + Number(item.miktar || 0);
                    batch.update(productDoc.ref, {
                        name: item.urun_adi || productDoc.data().name,
                        quantity: newQuantity,
                        salePrice: Number(item.satis_fiyati) || productDoc.data().salePrice,
                    });
                    const movementRef = doc(collection(productDoc.ref, 'movements'));
                    batch.set(movementRef, {
                        date: new Date(),
                        type: 'Excel ile Güncelleme',
                        quantity: `+${item.miktar} ${item.birim || 'Adet'}`
                    });
                } else {
                    const newProductRef = doc(productsRef);
                    batch.set(newProductRef, {
                        name: item.urun_adi,
                        barcode: String(item.barkod),
                        groupId: group ? group.id : '',
                        quantity: Number(item.miktar || 0),
                        unit: item.birim || 'Adet',
                        salePrice: Number(item.satis_fiyati || 0),
                        criticalLevel: Number(item.kritik_seviye || 10),
                        // Initialize empty warehouse quantities for new products
                        warehouseQuantities: {},
                        createdAt: new Date()
                    });
                    const movementRef = doc(collection(newProductRef, 'movements'));
                    batch.set(movementRef, {
                        date: new Date(),
                        type: 'Excel ile İlk Giriş',
                        quantity: `+${item.miktar} ${item.birim || 'Adet'}`
                    });
                }
            }

            try {
                await batch.commit();
                alert("Excel'den veri aktarımı başarıyla tamamlandı!");
                setShowExcelModal(false);
            } catch (error) {
                console.error("Excel aktarımı sırasında hata:", error);
                alert("Veri aktarımı sırasında bir hata oluştu.");
            } finally {
                setIsImporting(false);
            }
        };
        reader.readAsArrayBuffer(excelFile);
    };

    const handleBulkPriceUpdate = async ({ updateType, value, operation }) => {
        if (!userData?.companyId || selectedProductIds.length === 0) return;

        const companyId = userData.companyId;
        const batch = writeBatch(db);
        const selectedProducts = products.filter(p => selectedProductIds.includes(p.id));

        selectedProducts.forEach(product => {
            const productRef = doc(db, 'companies', companyId, 'products', product.id);
            let currentPrice = product.salePrice || 0;
            let newPrice = currentPrice;

            if (updateType === 'percentage') {
                const change = currentPrice * (value / 100);
                newPrice = operation === 'increase' ? currentPrice + change : currentPrice - change;
            } else if (updateType === 'fixed') {
                newPrice = operation === 'increase' ? currentPrice + value : currentPrice - value;
            } else if (updateType === 'set') {
                newPrice = value;
            }

            // Ensure price is not negative and formatted correctly
            newPrice = Math.max(0, Number(newPrice.toFixed(2)));

            batch.update(productRef, { salePrice: newPrice });
        });

        try {
            await batch.commit();
            alert(`${selectedProductIds.length} ürünün fiyatı başarıyla güncellendi.`);
            setShowBulkPriceModal(false);
            setSelectedProductIds([]);
        } catch (error) {
            console.error("Toplu fiyat güncelleme hatası:", error);
            alert("Fiyatlar güncellenirken bir hata oluştu.");
        }
    };

    const handleBulkStockUpdate = async ({ operation, amount, warehouseId, note }) => {
        if (!userData?.companyId || selectedProductIds.length === 0) return;

        const companyId = userData.companyId;
        const batch = writeBatch(db);
        const selectedProducts = products.filter(p => selectedProductIds.includes(p.id));
        const warehouse = warehouses.find(w => w.id === warehouseId);

        for (const product of selectedProducts) {
            const productRef = doc(db, 'companies', companyId, 'products', product.id);
            const movementRef = doc(collection(db, 'companies', companyId, 'products', product.id, 'movements'));

            let currentTotalQuantity = Number(product.quantity || 0);
            let warehouseQuantities = product.warehouseQuantities || {};
            let currentWarehouseQuantity = Number(warehouseQuantities[warehouseId] || 0);

            let newTotalQuantity = currentTotalQuantity;
            let newWarehouseQuantity = currentWarehouseQuantity;
            let movementAmount = amount;

            if (operation === 'increase') {
                newTotalQuantity = currentTotalQuantity + amount;
                newWarehouseQuantity = currentWarehouseQuantity + amount;
            } else if (operation === 'decrease') {
                newTotalQuantity = Math.max(0, currentTotalQuantity - amount);
                newWarehouseQuantity = Math.max(0, currentWarehouseQuantity - amount);
                movementAmount = -amount;
            } else if (operation === 'set') {
                newTotalQuantity = (currentTotalQuantity - currentWarehouseQuantity) + amount;
                newWarehouseQuantity = amount;
                movementAmount = amount - currentWarehouseQuantity;
            }

            // Update product document
            batch.update(productRef, {
                quantity: newTotalQuantity,
                [`warehouseQuantities.${warehouseId}`]: newWarehouseQuantity
            });

            // Create movement document
            batch.set(movementRef, {
                date: serverTimestamp(),
                type: movementAmount >= 0 ? 'Giriş (Toplu)' : 'Çıkış (Toplu)',
                quantity: `${movementAmount >= 0 ? '+' : '-'}${Math.abs(movementAmount)} ${product.unit || 'Adet'}`,
                previousQuantity: currentTotalQuantity,
                newQuantity: newTotalQuantity,
                warehouseId: warehouseId,
                warehouseName: warehouse?.name || 'Bilinmeyen Depo',
                note: note,
                productBarcode: product.barcode || '',
                productName: product.name
            });
        }

        try {
            await batch.commit();
            alert(`${selectedProductIds.length} ürünün stok miktarı başarıyla güncellendi.`);
            setShowBulkStockModal(false);
            setSelectedProductIds([]);
        } catch (error) {
            console.error("Toplu stok güncelleme hatası:", error);
            alert("Stoklar güncellenirken bir hata oluştu.");
        }
    };

    const handleTransferStock = async ({ fromWarehouseId, toWarehouseId, amount, note }) => {
        if (!userData?.companyId || selectedProductIds.length === 0) return;

        const companyId = userData.companyId;
        const batch = writeBatch(db);
        const selectedProducts = products.filter(p => selectedProductIds.includes(p.id));
        const fromWarehouse = warehouses.find(w => w.id === fromWarehouseId);
        const toWarehouse = warehouses.find(w => w.id === toWarehouseId);

        for (const product of selectedProducts) {
            const productRef = doc(db, 'companies', companyId, 'products', product.id);
            const movementRef = doc(collection(db, 'companies', companyId, 'products', product.id, 'movements'));

            let warehouseQuantities = { ...product.warehouseQuantities } || {};
            let fromQty = Number(warehouseQuantities[fromWarehouseId] || 0);
            let toQty = Number(warehouseQuantities[toWarehouseId] || 0);

            // Check if source warehouse has enough stock
            const transferAmount = Math.min(amount, fromQty);
            if (transferAmount <= 0) continue;

            warehouseQuantities[fromWarehouseId] = fromQty - transferAmount;
            warehouseQuantities[toWarehouseId] = toQty + transferAmount;

            // Update product document
            batch.update(productRef, {
                warehouseQuantities: warehouseQuantities
            });

            // Create movement document
            batch.set(movementRef, {
                date: serverTimestamp(),
                type: 'Transfer',
                quantity: `${transferAmount} ${product.unit || 'Adet'}`,
                fromWarehouseId,
                fromWarehouseName: fromWarehouse?.name || 'Bilinmeyen',
                toWarehouseId,
                toWarehouseName: toWarehouse?.name || 'Bilinmeyen',
                note: note,
                productBarcode: product.barcode || '',
                productName: product.name
            });
        }

        try {
            await batch.commit();
            alert(`${selectedProductIds.length} ürün için depolar arası transfer başarıyla tamamlandı.`);
            setShowTransferModal(false);
            setSelectedProductIds([]);
        } catch (error) {
            console.error("Transfer hatası:", error);
            alert("Transfer sırasında bir hata oluştu.");
        }
    };

    const handleExport = ({ format, showPrice }) => {
        if (selectedProductIds.length === 0) return;


        const selectedProducts = products.filter(p => selectedProductIds.includes(p.id));

        const dateStr = new Date().toLocaleDateString('tr-TR').replace(/\./g, '-');

        if (format === 'excel') {
            const exportData = selectedProducts.map(p => {
                const data = {
                    'Ürün Adı': p.name,
                    'Barkod': p.barcode || '',
                    'Grup': productGroups.find(g => g.id === p.groupId)?.name || '-',
                    'Stok Miktarı': p.quantity,
                    'Birim': p.unit || 'Adet',
                    'Kritik Seviye': p.criticalLevel || 0,
                    'Hammadde': p.isRawMaterial ? 'Evet' : 'Hayır'
                };

                if (showPrice) {
                    data['Satış Fiyatı'] = p.salePrice || 0;
                }

                return data;
            });

            const worksheet = XLSX.utils.json_to_sheet(exportData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Seçili Ürünler');

            XLSX.writeFile(workbook, `secili_urunler_${dateStr}.xlsx`);
        } else if (format === 'pdf') {
            try {
                const doc = new jsPDF();

                doc.setFontSize(18);
                doc.text('Ürün Listesi', 14, 22);
                doc.setFontSize(11);
                doc.text(`Tarih: ${new Date().toLocaleDateString('tr-TR')}`, 14, 30);

                const columns = [
                    { header: 'Ürün Adı', dataKey: 'name' },
                    { header: 'Barkod', dataKey: 'barcode' },
                    { header: 'Stok', dataKey: 'quantity' },
                    { header: 'Birim', dataKey: 'unit' },
                ];

                if (showPrice) {
                    columns.push({ header: 'Fiyat', dataKey: 'salePrice' });
                }

                const data = selectedProducts.map(p => {
                    const row = {
                        name: p.name,
                        barcode: p.barcode || '-',
                        quantity: p.quantity,
                        unit: p.unit || 'Adet'
                    };
                    if (showPrice) {
                        row.salePrice = `${(p.salePrice || 0).toFixed(2)} ₺`;
                    }
                    return row;
                });

                doc.autoTable({
                    head: [columns.map(c => c.header)],
                    body: data.map(row => columns.map(c => row[c.dataKey])),
                    startY: 40,
                    styles: { fontSize: 10, cellPadding: 3 },
                    headStyles: { fillColor: [99, 102, 241] },
                    alternateRowStyles: { fillColor: [248, 250, 252] }
                });

                doc.save(`secili_urunler_${dateStr}.pdf`);
            } catch (error) {
                console.error("PDF oluşturma hatası:", error);
                alert("PDF oluşturulurken bir hata oluştu.");
            }
        }

        setShowExportModal(false);

    };

    const handleStoreTransfer = async ({ fromWarehouseId, toStoreId, amount, note }) => {
        if (!userData?.companyId || selectedProductIds.length === 0) return;

        const companyId = userData.companyId;
        const batch = writeBatch(db);
        const selectedProducts = products.filter(p => selectedProductIds.includes(p.id));
        const fromWarehouse = warehouses.find(w => w.id === fromWarehouseId);
        const store = stores.find(s => s.id === toStoreId);

        // Optional: Save transfer record
        const transferRef = doc(collection(db, 'companies', companyId, 'storeTransfers'));
        const transferData = {
            from: "company",
            to: toStoreId,
            toStoreName: store?.name || store?.storeName || 'Bilinmeyen Mağaza',
            status: "completed",
            date: serverTimestamp(),
            note: note,
            items: selectedProducts.map(p => ({ productId: p.id, productName: p.name, quantity: amount }))
        };
        batch.set(transferRef, transferData);

        for (const product of selectedProducts) {
            // Update main product stock
            const productRef = doc(db, 'companies', companyId, 'products', product.id);
            const movementRef = doc(collection(db, 'companies', companyId, 'products', product.id, 'movements'));

            let warehouseQuantities = product.warehouseQuantities ? { ...product.warehouseQuantities } : {};
            let fromQty = Number(warehouseQuantities[fromWarehouseId] || 0);

            // Check if source warehouse has enough stock
            const transferAmount = Math.min(amount, fromQty);
            if (transferAmount <= 0) continue;

            warehouseQuantities[fromWarehouseId] = fromQty - transferAmount;
            const newTotalQty = (product.quantity || 0) - transferAmount;

            batch.update(productRef, {
                warehouseQuantities: warehouseQuantities,
                quantity: newTotalQty
            });

            // Create movement in main product
            batch.set(movementRef, {
                date: serverTimestamp(),
                type: 'Mağazaya Sevk',
                quantity: `-${transferAmount} ${product.unit || 'Adet'}`,
                fromWarehouseId,
                fromWarehouseName: fromWarehouse?.name || 'Bilinmeyen',
                toStoreId: toStoreId,
                toStoreName: store?.name || store?.storeName || 'Mağaza',
                note: note,
                productBarcode: product.barcode || '',
                productName: product.name
            });

            // Add to store inventory using increment to avoid concurrent update issues
            const storeInventoryRef = doc(db, 'companies', companyId, 'stores', toStoreId, 'inventory', product.id);
            batch.set(storeInventoryRef, {
                name: product.name,
                barcode: product.barcode || '',
                salePrice: product.salePrice || 0,
                unit: product.unit || 'Adet',
                imageUrl: product.imageUrl || '',
                groupId: product.groupId || '', // Optional
                quantity: increment(transferAmount),
                lastUpdated: serverTimestamp()
            }, { merge: true });
        }

        try {
            await batch.commit();
            alert(`${selectedProductIds.length} ürün başarıyla ${store?.name || store?.storeName} mağazasına sevk edildi.`);
            setShowStoreTransferModal(false);
            setSelectedProductIds([]);
        } catch (error) {
            console.error("Mağazaya sevk hatası:", error);
            alert("Mağazaya sevk işlemi sırasında hata oluştu!");
        }
    };

    return (
        <div style={styles.container}>
            <div style={{ width: '100%' }}>
                <div style={styles.header}>
                    <h1 style={styles.title}>Stok Yönetimi</h1>
                    <div style={styles.headerActions}>
                        <button onClick={() => setShowExcelModal(true)} style={{ ...styles.button, background: '#198754' }}>Excel İşlemleri</button>
                        <button onClick={() => setShowAddModal(true)} style={styles.button}>Yeni Ürün Ekle</button>
                    </div>
                </div>

                {/* Recent Movements Section */}
                {recentMovements.length > 0 && (
                    <div style={{ ...styles.box, marginBottom: '30px' }}>
                        <h2 style={{ ...styles.title, fontSize: '20px', marginBottom: '20px' }}>Son Stok Hareketleri</h2>
                        <div style={styles.tableContainer}>
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        <th style={styles.th}>Tarih</th>
                                        <th style={styles.th}>Ürün</th>
                                        <th style={styles.th}>İşlem</th>
                                        <th style={styles.th}>Miktar</th>
                                        <th style={styles.th}>Depo</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recentMovements.map(movement => (
                                        <tr key={movement.id} style={styles.trHover} onClick={() => navigate(`/stok/${movement.productId}`)}>
                                            <td style={styles.td}>
                                                {movement.date ? new Date(movement.date.seconds * 1000).toLocaleString('tr-TR') : '-'}
                                            </td>
                                            <td style={styles.td}>
                                                <div style={styles.productName}>{movement.productName}</div>
                                                <div style={{ fontSize: '12px', color: '#64748b' }}>{movement.productBarcode}</div>
                                            </td>
                                            <td style={styles.td}>{movement.type}</td>
                                            <td style={{ ...styles.td, fontWeight: '600' }}>{movement.quantity}</td>
                                            <td style={styles.td}>{movement.warehouseName || '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                <div style={styles.box}>
                    <div style={{ ...styles.listHeader, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                            <input type="text" placeholder="Ürün adı veya barkod ara..." style={styles.searchInput} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <input
                                    type="checkbox"
                                    id="rawMaterialsFilter"
                                    checked={showRawMaterialsOnly}
                                    onChange={(e) => setShowRawMaterialsOnly(e.target.checked)}
                                />
                                <label htmlFor="rawMaterialsFilter" style={{ fontSize: '14px', color: '#374151', cursor: 'pointer' }}>
                                    Sadece Hammaddeler
                                </label>
                            </div>
                        </div>
                        {selectedProductIds.length > 0 && (
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                    onClick={() => {
                                        const selectedProducts = products.filter(p => selectedProductIds.includes(p.id));
                                        setPrintProducts(selectedProducts);
                                    }}
                                    style={{
                                        ...styles.button,
                                        background: '#06C167',
                                        padding: '8px 16px',
                                        fontSize: '13px'
                                    }}
                                >
                                    🖨️ Toplu Barkod ({selectedProductIds.length})
                                </button>
                                <button
                                    onClick={() => setShowBulkPriceModal(true)}
                                    style={{
                                        ...styles.button,
                                        background: '#f59e0b',
                                        padding: '8px 16px',
                                        fontSize: '13px'
                                    }}
                                >
                                    💰 Fiyat Güncelle ({selectedProductIds.length})
                                </button>
                                <button
                                    onClick={() => setShowBulkStockModal(true)}
                                    style={{
                                        ...styles.button,
                                        background: '#10b981',
                                        padding: '8px 16px',
                                        fontSize: '13px'
                                    }}
                                >
                                    📦 Stok Güncelle ({selectedProductIds.length})
                                </button>
                                <button
                                    onClick={() => setShowTransferModal(true)}
                                    style={{
                                        ...styles.button,
                                        background: '#3b82f6',
                                        padding: '8px 16px',
                                        fontSize: '13px'
                                    }}
                                >
                                    🔄 Transfer ({selectedProductIds.length})
                                </button>
                                <button
                                    onClick={() => setShowStoreTransferModal(true)}
                                    style={{
                                        ...styles.button,
                                        background: '#8b5cf6',
                                        padding: '8px 16px',
                                        fontSize: '13px'
                                    }}
                                >
                                    🚀 Mağazaya Sevk Et ({selectedProductIds.length})
                                </button>
                                <button
                                    onClick={() => setShowExportModal(true)}
                                    style={{
                                        ...styles.button,
                                        background: '#059669',
                                        padding: '8px 16px',
                                        fontSize: '13px'
                                    }}
                                >
                                    📤 Dışa Aktar ({selectedProductIds.length})
                                </button>
                            </div>
                        )}
                    </div>
                    {loading ? <p>Yükleniyor...</p> : (
                        <div style={styles.tableContainer}>
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        <th style={styles.th}>
                                            <input
                                                type="checkbox"
                                                checked={sortedProducts.length > 0 && selectedProductIds.length === sortedProducts.length}
                                                onChange={toggleAllSelection}
                                                style={{ cursor: 'pointer' }}
                                            />
                                        </th>
                                        <th style={styles.th}>Görsel</th>
                                        <th style={styles.th} onClick={() => requestSort('name')}>Ürün Adı{getSortIndicator('name')}</th>
                                        <th style={styles.th} onClick={() => requestSort('quantity')}>Stok{getSortIndicator('quantity')}</th>
                                        {warehouses.filter(w => w.isActive).length > 0 && (
                                            <th style={styles.th}>Depo Detayları</th>
                                        )}
                                        <th style={styles.th}>Blokaj</th>
                                        <th style={styles.th} onClick={() => requestSort('salePrice')}>Satış Fiyatı{getSortIndicator('salePrice')}</th>
                                        <th style={styles.th}>İşlemler</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedProducts.map(p => (
                                        <tr
                                            key={p.id}
                                            style={{
                                                ...styles.trHover,
                                                background: selectedProductIds.includes(p.id) ? '#f0f4ff' : 'transparent'
                                            }}
                                            onClick={() => navigate(`/stok/${p.id}`)}
                                        >
                                            <td style={styles.td} onClick={(e) => e.stopPropagation()}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedProductIds.includes(p.id)}
                                                    onChange={() => toggleProductSelection(p.id)}
                                                    style={{ cursor: 'pointer' }}
                                                />
                                            </td>
                                            <td style={styles.td}>
                                                {p.imageUrl ? (
                                                    <img
                                                        src={p.imageUrl}
                                                        alt={p.name}
                                                        style={{
                                                            width: '40px',
                                                            height: '40px',
                                                            borderRadius: '4px',
                                                            objectFit: 'cover',
                                                            border: '1px solid #dee2e6'
                                                        }}
                                                    />
                                                ) : (
                                                    <div style={{
                                                        width: '40px',
                                                        height: '40px',
                                                        borderRadius: '4px',
                                                        background: '#f8f9fa',
                                                        border: '1px solid #dee2e6',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: '#6c757d',
                                                        fontSize: '12px'
                                                    }}>
                                                        📦
                                                    </div>
                                                )}
                                            </td>
                                            <td style={styles.td}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    {p.isRawMaterial && (
                                                        <span style={{
                                                            background: '#dbeafe',
                                                            color: '#1d4ed8',
                                                            padding: '2px 6px',
                                                            borderRadius: '4px',
                                                            fontSize: '10px',
                                                            fontWeight: '600',
                                                            whiteSpace: 'nowrap'
                                                        }}>
                                                            HAMMADDE
                                                        </span>
                                                    )}
                                                    <span>{p.name}</span>
                                                </div>
                                            </td>
                                            <td style={{ ...styles.td, ...(p.quantity <= p.criticalLevel && styles.criticalStock) }}>{p.quantity} {p.unit || 'Adet'}</td>
                                            {warehouses.filter(w => w.isActive).length > 0 && (
                                                <td style={styles.td}>
                                                    {p.warehouseQuantities ? (
                                                        Object.entries(p.warehouseQuantities).map(([warehouseId, quantity]) => {
                                                            const warehouse = warehouses.find(w => w.id === warehouseId);
                                                            return warehouse && quantity > 0 ? (
                                                                <div key={warehouseId} style={{ fontSize: '12px', marginBottom: '2px' }}>
                                                                    {warehouse.name}: {quantity}
                                                                </div>
                                                            ) : null;
                                                        })
                                                    ) : (
                                                        <span>-</span>
                                                    )}
                                                </td>
                                            )}
                                            <td style={{ ...styles.td, color: (p.blockedQuantity > 0) ? '#dc3545' : '#28a745', fontWeight: '600' }}>
                                                {p.blockedQuantity || 0} {p.unit || 'Adet'}
                                            </td>
                                            <td style={styles.td}>{canViewPrices ? `${(p.salePrice || 0).toFixed(2)} ₺` : '***'}</td>
                                            <td style={styles.td} onClick={(e) => e.stopPropagation()}>
                                                <button
                                                    onClick={() => setPrintProduct(p)}
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        cursor: 'pointer',
                                                        fontSize: '20px',
                                                        padding: '4px',
                                                        borderRadius: '4px',
                                                        transition: 'background 0.2s'
                                                    }}
                                                    title="Barkod Yazdır"
                                                    onMouseOver={(e) => e.currentTarget.style.background = '#f1f5f9'}
                                                    onMouseOut={(e) => e.currentTarget.style.background = 'none'}
                                                >
                                                    🖨️
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
            {showAddModal && <AddProductModal onClose={() => setShowAddModal(false)} onSave={handleAddProduct} productGroups={productGroups} warehouses={warehouses} />}
            {showExcelModal && <ExcelImportModal onClose={() => setShowExcelModal(false)} onImport={handleImport} onDownloadTemplate={downloadTemplate} isImporting={isImporting} />}
            {showBulkPriceModal && (
                <BulkPriceUpdateModal
                    onClose={() => setShowBulkPriceModal(false)}
                    onUpdate={handleBulkPriceUpdate}
                    selectedCount={selectedProductIds.length}
                />
            )}
            {showBulkStockModal && (
                <BulkStockUpdateModal
                    onClose={() => setShowBulkStockModal(false)}
                    onUpdate={handleBulkStockUpdate}
                    warehouses={warehouses}
                    selectedCount={selectedProductIds.length}
                />
            )}
            {showExportModal && (
                <ExportOptionsModal
                    onClose={() => setShowExportModal(false)}
                    onExport={handleExport}
                    selectedCount={selectedProductIds.length}
                />
            )}


            {showTransferModal && (
                <TransferStockModal
                    onClose={() => setShowTransferModal(false)}
                    onTransfer={handleTransferStock}
                    warehouses={warehouses}
                    products={products}
                    selectedProductIds={selectedProductIds}
                />
            )}
            
            {showStoreTransferModal && (
                <StoreTransferModal
                    onClose={() => setShowStoreTransferModal(false)}
                    onTransfer={handleStoreTransfer}
                    warehouses={warehouses}
                    stores={stores}
                    products={products}
                    selectedProductIds={selectedProductIds}
                />
            )}

            {printProduct && (
                <BarcodePrintModal
                    product={printProduct}
                    onClose={() => setPrintProduct(null)}
                    canViewPrices={canViewPrices}
                />
            )}
            {printProducts && (
                <BarcodePrintModal
                    products={printProducts}
                    onClose={() => {
                        setPrintProducts(null);
                        setSelectedProductIds([]);
                    }}
                    canViewPrices={canViewPrices}
                />
            )}

        </div>
    );
}

export default Stock;
